import React, { useState, useEffect } from 'react';
import {
  Container, Typography, TextField, MenuItem, Button, Box,
  Paper, Stack, Card, CardContent, CardActions,
  Checkbox, FormControlLabel
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { api } from '../../api';
import { getCurrentUser } from '../../services/auth';
import { validateName, validatePhone, runValidators, PHONE_RULE_KEY } from '../../utils/validation';
import dayjs from 'dayjs';

export default function CreateBooking() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const date = params.get('date');
  const initialTime = params.get('time');
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [userId, setUserId] = useState(null);
  const [masters, setMasters] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showSlots, setShowSlots] = useState(false);
  const [sendSms, setSendSms] = useState(true);   // авто-надсилання лінка SMS
  const [sendEmail, setSendEmail] = useState(true); // ...та email
  const [clientEmail, setClientEmail] = useState(''); // email існуючого клієнта (для чекбокса Email)
  const [price, setPrice] = useState('');            // ціна запису (порожнє = сума послуг)
  const [priceEdited, setPriceEdited] = useState(false); // майстер вручну змінив ціну

  // Автопідстановка ціни = сума обраних послуг, поки майстер не змінив вручну
  useEffect(() => {
    if (priceEdited) return;
    const sum = services
      .filter(s => selected.includes(s.id))
      .reduce((acc, s) => acc + Number(s.price || 0), 0);
    setPrice(selected.length ? String(sum) : '');
  }, [selected, services, priceEdited]);

  // Гарний підпис дати (день тижня + дата) для банера «Час запису»
  const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const whenLabel = date
    ? `${t('dashboard.' + WEEKDAY_KEYS[dayjs(date).day()])}, ${dayjs(date).format('DD.MM.YYYY')}`
    : '';

  // Перевірка чи обрана дата/час в минулому (для UI)
  const isPast = (() => {
    if (!date || !initialTime) return false;
    const bookingDateTime = dayjs(`${date}T${initialTime}`);
    return bookingDateTime.isBefore(dayjs());
  })();

  // Завантажити майстрів лише своєї організації
  useEffect(() => {
    async function fetchMasters() {
      try {
        const user = await getCurrentUser();
        if (!user) {
          setError(t('createBooking.orgNotFound'));
          setMasters([]);
          return;
        }
        const allUsers = await api.getUsers();
        const mastersList = allUsers.filter(u => u.role === 'master');
        setMasters(mastersList);
      } catch (e) {
        setError(t('createBooking.mastersLoadFailed'));
        setMasters([]);
      }
    }
    fetchMasters();
  }, []);

  // Завантажити послуги для обраного майстра
  useEffect(() => {
    async function fetchServices() {
      if (selectedMaster) {
        try {
          const filteredServices = await api.getServices(selectedMaster);
          setServices(filteredServices);
          setSelected([]);
        } catch {
          setServices([]);
        }
      } else {
        setServices([]);
      }
    }
    fetchServices();
  }, [selectedMaster]);

  // Пошук користувача по телефону для автозаповнення імені
  const handlePhoneCheck = async () => {
    if (!phone) return;
    try {
      const found = await api.getUserByPhone(phone);
      if (found) {
        setName(found.name || '');
        setUserId(found.id);
        setClientEmail(found.email || '');
      } else {
        setName('');
        setUserId(null);
        setClientEmail('');
      }
    } catch {
      setName('');
      setUserId(null);
      setClientEmail('');
    }
  };

  // Знайти доступні таймслоти
  const findAvailableSlots = (bookings, duration, day, selectedTime) => {
    const allSlots = [];
    for (let h = 7; h < 21; h++) {
      for (let m = 0; m < 60; m += 15) {
        const start = dayjs(`${day}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        const end = start.add(duration, 'minute');
        if (end.hour() >= 21 && end.minute() > 0) continue;
        const overlap = bookings.some(b => {
          const bStart = dayjs(b.date).hour(Number(b.time.slice(0,2))).minute(Number(b.time.slice(3,5)));
          const bDuration = b.durationMinutes ?? (Array.isArray(b.services)
            ? b.services.reduce((acc, s) => acc + Number(s.blockDuration ?? s.duration ?? 0), 0)
            : 0);
          const bEnd = bStart.add(bDuration, 'minute');
          return start.isBefore(bEnd) && bStart.isBefore(end);
        });
        if (!overlap) {
          allSlots.push(start.format('HH:mm'));
        }
      }
    }
    // Вибране користувачем початкове значення
    const idx = allSlots.findIndex(t => t === selectedTime);
    const from = Math.max(0, idx - 5);
    const to = Math.min(allSlots.length, idx + 10);
    return allSlots.slice(from, to);
  };

  // Створити запис
  const handleCreate = async (customTime) => {
    setError('');
    setAvailableSlots([]);
    setShowSlots(false);
    const finalTime = customTime || initialTime;

    // --- Перевірка на минуле (додатково) ---
    const now = dayjs();
    const bookingDateTime = dayjs(`${date}T${finalTime}`);
    if (bookingDateTime.isBefore(now)) {
      setError(t('createBooking.pastError'));
      return;
    }

    const fErrs = runValidators({
      name: validateName(name),
      phone: validatePhone(phone),
    });
    setFieldErrors(fErrs);
    if (Object.keys(fErrs).length > 0) {
      setError(t('createBooking.checkNamePhone'));
      return;
    }
    if (selected.length === 0 || !selectedMaster) {
      setError(t('createBooking.selectMasterService'));
      return;
    }
    setLoading(true);

    try {
      let user = null;
      if (!userId) {
        // Створюємо нового користувача, якщо не знайдений
        const data = await api.createUser({ name, phone, role: 'guest' });
        user = {
          ...data?.user || {},
        };
      } else {
        user = {
          id: userId,
          name,
          phone
        };
      }

      const selectedServices = services.filter(s => selected.includes(s.id));
      // Слід у календарі = час бронювання (blockDuration), не час роботи
      const duration = selectedServices.reduce((acc, s) => acc + Number(s.blockDuration ?? s.duration ?? 0), 0);
      const day = dayjs(date).format('YYYY-MM-DD');
      const allBookings = await api.getBookings();
      const bookingsSameMasterSameDay = allBookings
        .filter(b => b.masterId === Number(selectedMaster) && dayjs(b.date).format('YYYY-MM-DD') === day);

      const newStart = dayjs(`${date}T${finalTime}`);
      const newEnd = newStart.add(duration, 'minute');
      const conflict = bookingsSameMasterSameDay.some(b => {
        const bStart = dayjs(b.date).hour(Number(b.time.slice(0,2))).minute(Number(b.time.slice(3,5)));
        const bDuration = b.durationMinutes ?? (Array.isArray(b.services)
          ? b.services.reduce((acc, s) => acc + Number(s.blockDuration ?? s.duration ?? 0), 0)
          : 0);
        const bEnd = bStart.add(bDuration, 'minute');
        return newStart.isBefore(bEnd) && bStart.isBefore(newEnd);
      });

      if (conflict) {
        const slots = findAvailableSlots(bookingsSameMasterSameDay, duration, day, initialTime);
        setAvailableSlots(slots);
        setError(t('createBooking.timeTaken'));
        setShowSlots(true);
        setLoading(false);
        return;
      }

      const payload = {
        masterId: Number(selectedMaster),
        userId: user.id,
        date,
        time: finalTime,
        services: selected,
        price: price === '' ? null : Number(String(price).trim().replace(',', '.')),
        sendSms: !!phone.trim() && sendSms,
        sendEmail: !!clientEmail && sendEmail,
      };
      const created = await api.createBooking(payload);
      // Після створення одразу повертаємось у панель із записами.
      // Лінк-підтвердження й статус розсилки передаємо через state — панель
      // покаже короткий тост із можливістю скопіювати лінк (нічого не губиться).
      navigate('/master/dashboard', {
        state: {
          created: {
            confirmUrl: created?.confirmUrl || '',
            notified: created?.notified || null,
            clientName: name || phone,
            date,
            time: finalTime,
          },
        },
      });
    } catch (e) {
      setError(e.message || t('createBooking.createError'));
    }
    setLoading(false);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleGoBackCalendar = () => {
    navigate('/master/dashboard');
  };

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#faf7f2', color: '#1a1a16' }}>
        <Typography variant="h6" gutterBottom>{t('createBooking.title')}</Typography>

        {/* Банер обраної дати/часу — щоб одразу видно було, куди саме створюється запис */}
        {date && initialTime && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5,
              bgcolor: '#efe7d6', border: '1px solid #c9a24a', borderRadius: 2,
            }}
          >
            <Typography sx={{ fontSize: '1.6rem', lineHeight: 1 }}>🗓️</Typography>
            <Box>
              <Typography variant="caption" sx={{ color: '#57564d', display: 'block' }}>
                {t('createBooking.whenLabel')}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: '#1a1a16' }}>
                {whenLabel} · {initialTime}
              </Typography>
            </Box>
          </Paper>
        )}

        <TextField
          label={t('common.phone')}
          fullWidth
          value={phone}
          placeholder="+48601234567"
          onChange={e => { setPhone(e.target.value); setFieldErrors(p => ({ ...p, phone: '' })); }}
          onBlur={handlePhoneCheck}
          error={!!fieldErrors.phone} helperText={t(fieldErrors.phone || PHONE_RULE_KEY)}
          sx={{ mt: 2 }}
        />

        <TextField
          label={t('common.name')}
          fullWidth
          value={name}
          onChange={e => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: '' })); }}
          disabled={!!userId}
          error={!!fieldErrors.name} helperText={fieldErrors.name ? t(fieldErrors.name) : ' '}
          sx={{ mb: 1 }}
        />

        <TextField
          label={t('createBooking.master')}
          select
          fullWidth
          value={selectedMaster}
          onChange={e => setSelectedMaster(e.target.value)}
          sx={{ mb: 2 }}
        >
          {masters.length === 0 && (
            <MenuItem disabled value="">{t('createBooking.noMasters')}</MenuItem>
          )}
          {masters.map(m => (
            <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
          ))}
        </TextField>

        {services.length > 0 && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>{t('createBooking.servicesLabel')}</Typography>
            <Stack spacing={2} mb={2}>
              {services.map(s => (
                <Card
                  key={s.id}
                  sx={{
                    backgroundColor: selected.includes(s.id) ? '#c9a24a' : '#efeae2',
                    color: '#1a1a16',
                    borderRadius: 2,
                    boxShadow: selected.includes(s.id) ? 6 : 2
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold">{pickServiceName(s, i18n.language)}</Typography>
                    <Typography variant="body2">{t('createBooking.duration', { duration: s.duration })}</Typography>
                    <Typography variant="body2">{t('createBooking.price', { price: s.price ? formatMoney(s.price) : '—' })}</Typography>
                  </CardContent>
                  <CardActions>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected.includes(s.id)}
                          onChange={() => setSelected(prev =>
                            prev.includes(s.id)
                              ? prev.filter(i => i !== s.id)
                              : [...prev, s.id])}
                          sx={{ color: '#1a1a16' }}
                        />
                      }
                      label={t('createBooking.choose')}
                    />
                  </CardActions>
                </Card>
              ))}
            </Stack>
          </>
        )}

        {/* Ціна запису — майстер може поставити довільну суму (0 для моделі, знижка тощо) */}
        {selected.length > 0 && (
          <TextField
            label={t('createBooking.priceLabel')}
            fullWidth
            value={price}
            onChange={e => { setPrice(e.target.value); setPriceEdited(true); }}
            inputProps={{ inputMode: 'decimal' }}
            helperText={t('createBooking.priceHint')}
            sx={{ mb: 2 }}
          />
        )}

        {/* Блокування створення якщо дата/час у минулому */}
        {isPast && (
          <Typography color="error" sx={{ mb: 1 }}>
            {t('createBooking.pastError')}
          </Typography>
        )}

        {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}

        {showSlots && availableSlots.length > 0 && (
          <Stack spacing={1} sx={{ my: 2 }}>
            <Typography>{t('createBooking.nearestSlots')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {availableSlots.map(t => (
                <Button
                  key={t}
                  variant={t === initialTime ? 'contained' : 'outlined'}
                  size="small"
                  sx={{ mb: 1 }}
                  onClick={() => handleCreate(t)}
                >
                  {t}
                </Button>
              ))}
            </Stack>
            <Button variant="text" onClick={handleGoBackCalendar} sx={{ mt: 1, color: '#1a1a16', borderColor: '#1a1a16' }}>
              {t('createBooking.backToCalendar')}
            </Button>
          </Stack>
        )}

        {(phone.trim() || clientEmail) && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>{t('createBooking.autoSendLabel')}</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {phone.trim() && (
                <FormControlLabel
                  control={<Checkbox checked={sendSms} onChange={e => setSendSms(e.target.checked)} sx={{ color: '#1a1a16' }} />}
                  label={t('createBooking.sendSms')}
                />
              )}
              {clientEmail && (
                <FormControlLabel
                  control={<Checkbox checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} sx={{ color: '#1a1a16' }} />}
                  label={t('createBooking.sendEmail')}
                />
              )}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" color="inherit" fullWidth onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleCreate()}
            fullWidth
            disabled={loading || isPast}
          >
            {loading ? t('createBooking.creating') : t('createBooking.title')}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
